/* Reusable functions for account app */
import {
  ONBOARDING_NUM_STEPS_PER_EXTRA_PET,
  ONBOARDING_NUM_STEPS_SINGLE_PET,
  ONBOARDING_STEPS,
} from './onboarding/enums';

import {groupOrderPetMeals} from '@utils/common_utils';

import {
  DEFAULT_FIXED_SKU_GRAMS_BY_PET,
  DEFAULT_NUM_PACKS_OPTIONS_FOR_FLEXIBLE,
  IS_PROD,
  ORDER_CHARGE_STATUS,
  ORDER_SOURCE,
  ORDER_STATUS,
  PET_FEATURES,
  PET_SURVEYS,
  PET_TYPE,
  TRIAL_TYPES,
} from '@variables';

/* Determines how many cups to feed for fixed SKU pets based on pet weight. */
const getFixedSkuFeedingPackInstructions = (weight, activityMultiplier = 1.1) => {
  // Portions for high and very high activity level pets
  if (activityMultiplier === 1.3 || activityMultiplier === 1.5) {
    if (weight <= 10) {
      return {topper: '⅓', half: '¾', full: '1½'};
    }
    if (weight <= 15) {
      return {topper: '½', half: '1', full: '2'};
    }
    if (weight <= 20) {
      return {topper: '¾', half: '1½', full: '3'};
    }
    if (weight <= 30) {
      return {topper: '1', half: '2', full: '4'};
    }
    if (weight <= 40) {
      return {topper: '1¼', half: '2½', full: '5'};
    }
    if (weight <= 50) {
      return {topper: '1½', half: '3', full: '6'};
    }
    if (weight <= 75) {
      return {topper: '2¼', half: '4½', full: '9'};
    }
    if (weight <= 100) {
      return {topper: '3', half: '6', full: '12'};
    }
    return {topper: '3½', half: '7', full: '14'};
  }

  // Portions for low (regular) activity level pets
  if (weight <= 10) {
    return {topper: '¼', half: '½', full: '1'};
  }
  if (weight <= 15) {
    return {topper: '⅜', half: '¾', full: '1½'};
  }
  if (weight <= 20) {
    return {topper: '½', half: '1', full: '2'};
  }
  if (weight <= 30) {
    return {topper: '¾', half: '1½', full: '3'};
  }
  if (weight <= 40) {
    return {topper: '1', half: '2', full: '4'};
  }
  if (weight <= 50) {
    return {topper: '1¼', half: '2½', full: '5'};
  }
  if (weight <= 75) {
    return {topper: '1⅞', half: '3¾', full: '7½'};
  }
  if (weight <= 100) {
    return {topper: '2½', half: '5', full: '10'};
  }
  return {topper: '3', half: '6', full: '12'};
};

/* Get number of completed pet surveys.*/
const countCompletedSurveys = (pet, petSurvey) => {
  const petSurveysList = PET_SURVEYS[pet.petType];
  let result = 0;
  if (petSurvey) {
    petSurveysList.forEach((survey) => {
      if (petSurvey[`${survey}CompletedDate`]) {
        result++;
      }
    });
  }
  return result;
};

const countRemainingSurveys = (pet, petSurvey) => {
  return PET_SURVEYS[pet.petType].length - countCompletedSurveys(pet, petSurvey);
};

/* Builds an object mapping coupon stripeCouponId to coupon objects.*/
const buildCouponsMap = (selectableCoupons) =>
  selectableCoupons.reduce((obj, coupon) => ((obj[coupon.stripeCouponId] = coupon), obj), {});

/* Prepopulated subject and body in mailto for self-cancellation */
const selfCancellationHref = (email) =>
  'mailto:care@nomnomnow.com' +
  '?subject=Please cancel the recurring subscription for the Nom Nom account' +
  ` for ${email} effective immediately` +
  '&body=Please provide details to submit your request for cancellation:%0D%0A%0D%0A' +
  `I would like to cancel the subscription for ${email} because...`;

const hasFeatureFoodPersonalized = (pet) => pet.features.includes(PET_FEATURES.FOOD_PERSONALIZED);
const hasFeatureFoodFixedSku = (pet) => pet.features.includes(PET_FEATURES.FOOD_FIXED_SKU);
const isEligibleToChangeSubscription = (pet) => {
  if (!pet.isFoodSubscriber && (pet.petType === PET_TYPE.CAT || pet.deleted)) {
    return false;
  }
  return true;
};

const isEligibleToReferFriends = (pets, numCustomerOrders) => {
  return pets.some((pet) => pet.petType === PET_TYPE.DOG) && numCustomerOrders > 0;
};

/* Calculate onboarding progress number of steps */
const getProgressSteps = (step, petIndex, numPets, hasTransitionPages, needsTransitionNumPets) => {
  const steps = hasTransitionPages
    ? Object.values(ONBOARDING_STEPS)
    : Object.values(ONBOARDING_STEPS).filter((step) => step !== 'transitioning');
  const extraPets = numPets - 1;
  if (step === ONBOARDING_STEPS.unboxing) {
    return steps.indexOf(step) + petIndex;
  }
  if (step === ONBOARDING_STEPS.storing) {
    return steps.indexOf(step) + extraPets;
  }
  if (step === ONBOARDING_STEPS.transitioning) {
    return steps.indexOf(step) + petIndex + extraPets;
  }
  if (step === ONBOARDING_STEPS.serving) {
    return hasTransitionPages
      ? steps.indexOf(step) + petIndex + extraPets + needsTransitionNumPets - 1
      : steps.indexOf(step) + petIndex + extraPets;
  }

  return hasTransitionPages
    ? // eslint-disable-next-line max-len
      steps.indexOf(step) +
        extraPets * ONBOARDING_NUM_STEPS_PER_EXTRA_PET +
        needsTransitionNumPets -
        1
    : steps.indexOf(step) + extraPets * ONBOARDING_NUM_STEPS_PER_EXTRA_PET;
};

/* Calculate progress percentage for onboarding app based on number of steps */
const calculateOnboardingProgress = (
  step,
  numPets,
  petIndex,
  hasTransitionPages,
  needsTransitionNumPets
) => {
  const totalSteps = hasTransitionPages
    ? // eslint-disable-next-line max-len
      ONBOARDING_NUM_STEPS_SINGLE_PET +
      ONBOARDING_NUM_STEPS_PER_EXTRA_PET * (numPets - 1) +
      needsTransitionNumPets
    : ONBOARDING_NUM_STEPS_SINGLE_PET + ONBOARDING_NUM_STEPS_PER_EXTRA_PET * (numPets - 1);
  return (
    (getProgressSteps(step, petIndex, numPets, hasTransitionPages, needsTransitionNumPets) * 100) /
    totalSteps
  );
};

const filterOrderPetMealsForOnboardingStep = (orderPetMeals, onboardingStep) => {
  if (onboardingStep === ONBOARDING_STEPS.transitioning) {
    // Only include transition recipes
    return orderPetMeals.filter((orderPetMeal) => orderPetMeal.needsTransition);
  }

  // Only include non-transition recipes and transition recipes that contain multiple
  // meal bucket sizes (indicating there are non-transition packs of that recipe)
  return orderPetMeals.filter((orderPetMeal) => {
    return (
      !orderPetMeal.needsTransition ||
      (orderPetMeal.needsTransition && orderPetMeal.mealBuckets.length > 1)
    );
  });
};

/* Return list of recipes and gram size strings for transitioning and serving instructions */
const buildRecipesAndGramsList = (orderPetMeals, recipesMap, onboardingStep) => {
  // TODO(geraldiner): revisit after full transition meals deprecation
  // Consider new pets that were set skip_transition to False before the change to default True
  // and haven't had the first order yet so will have transition meals prepared
  // Post transition meals deprecation, there should be no transition meals, so only full portion
  // meal packs that would be used for transitioning and serving.
  const filteredOrderPetMeals = filterOrderPetMealsForOnboardingStep(orderPetMeals, onboardingStep);
  if (onboardingStep === ONBOARDING_STEPS.transitioning) {
    const finalOrderPetMeals = filteredOrderPetMeals.length ? filteredOrderPetMeals : orderPetMeals;
    return finalOrderPetMeals.map((orderPetMeal) => {
      return `${recipesMap[orderPetMeal.recipe.name].displayName} (${
        orderPetMeal.mealBuckets[0].grams
      }g)`;
    });
  }

  // For serving instructions, use full portion versions for recipe weight
  return filteredOrderPetMeals.map((orderPetMeal) => {
    return `${orderPetMeal.mealsPerDay} x ${recipesMap[orderPetMeal.recipe.name].displayName}
      (${
        orderPetMeal.needsTransition &&
        orderPetMeal.mealBuckets[1].grams > orderPetMeal.mealBuckets[0].grams
          ? orderPetMeal.mealBuckets[1].grams
          : orderPetMeal.mealBuckets[0].grams
      }g) meal pack${orderPetMeal.mealsPerDay > 1 ? 's' : ''}`;
  });
};

/* Return onboarding serving instructions text and corresponding image name */
const buildOnboardingServingDetails = (orderPetMeals, pet, recipesMap) => {
  if (hasFeatureFoodFixedSku(pet)) {
    const {topper, half, full} = getFixedSkuFeedingPackInstructions(
      pet.weight,
      pet.activityMultiplier
    );
    return [
      [
        `${topper} cup${topper === '1' ? '' : 's'} for topper portions`,
        `${half} cup${half === '1' ? '' : 's'} for half portions`,
        `${full} cup${full === '1' ? '' : 's'} for full portions.`,
      ],
      'regular_full_pour',
    ];
  }

  const recipesTextList = buildRecipesAndGramsList(
    orderPetMeals,
    recipesMap,
    ONBOARDING_STEPS.serving
  );

  // Show specific illustration for number of meal bags per meal if one exists,
  // assuming two meals a day
  if (
    Number.isInteger(orderPetMeals[0].mealsPerDay / 2) &&
    orderPetMeals[0].mealsPerDay / 2 > 1 &&
    orderPetMeals[0].mealsPerDay / 2 < 5
  ) {
    return [recipesTextList, `servings_regular_${orderPetMeals[0].mealsPerDay / 2}_full_pours`];
  }
  if (orderPetMeals[0].mealsPerDay === 1) {
    return [recipesTextList, 'servings_regular_half_pour'];
  }

  // Show single bag pour illustration if more specific one doesn't exist
  return [recipesTextList, 'regular_full_pour'];
};

const getOnboardingPageName = (customer, isAdmin, path) => {
  if (!path) {
    return '';
  }
  const splitWord = isAdmin ? `${customer.key.urlsafe}/` : 'onboarding/';
  return path.split(splitWord).pop().split('/')[0];
};

/* Determine if all pets in order have non-transition meals for serving instructions */
const isValidOrderForOnboarding = (order) => {
  if (!order) {
    return false;
  }
  const groupedOrderPetMeals = groupOrderPetMeals(order.orderPetMeals);
  return (
    Object.values(groupedOrderPetMeals).length &&
    Object.values(groupedOrderPetMeals).every((orderPetMeals) => {
      const servingOrderPetMeals = filterOrderPetMealsForOnboardingStep(
        orderPetMeals,
        ONBOARDING_STEPS.serving
      );
      return servingOrderPetMeals.length > 0;
    })
  );
};

const STRIPE_INVOICE_URL = IS_PROD
  ? 'https://dashboard.stripe.com/invoices/'
  : 'https://dashboard.stripe.com/test/invoices/';

/* Determine whether an order was charged. */
const isOrderCharged = (order) => {
  if (order.lastChargeStatus === ORDER_CHARGE_STATUS.SUCCEEDED) {
    return true;
  }
  if (order.charges?.find((charge) => charge.status === ORDER_CHARGE_STATUS.SUCCEEDED)) {
    return true;
  }

  return false;
};

/* Determine whether order has shipped. */
const orderHasShipped = (order) => {
  return ['shipped', 'delivery failed', 'delivered'].includes(order.status);
};

const allSubscribedPetsHaveFeatureFoodFixedSku = (pets) => {
  const subscribedFoodPets = pets.filter((pet) => pet.isFoodSubscriber);
  return subscribedFoodPets.length && subscribedFoodPets.every(hasFeatureFoodFixedSku);
};

const allSubscribedPetsHaveFeatureFoodPersonalized = (pets) => {
  const subscribedFoodPets = pets.filter((pet) => pet.isFoodSubscriber);
  return subscribedFoodPets.length && subscribedFoodPets.every(hasFeatureFoodPersonalized);
};

const isOnFlexibleMealPlan = (pets) => {
  return allSubscribedPetsHaveFeatureFoodFixedSku(pets);
};

const isOnPersonalizedMealPlan = (pets) => {
  return allSubscribedPetsHaveFeatureFoodPersonalized(pets);
};

const isCustomizedMealPlanItem = (mealPlanItem, petType) => {
  return (
    mealPlanItem.gramsBucket !== DEFAULT_FIXED_SKU_GRAMS_BY_PET[petType] ||
    !DEFAULT_NUM_PACKS_OPTIONS_FOR_FLEXIBLE.includes(mealPlanItem.mealBags)
  );
};

const isInOneTimeTrial = (customer) => {
  return customer.isInTrialMode && customer.trialType === TRIAL_TYPES.TRIAL_TYPE_ONE_TIME;
};

const hasMoreThanOneDog = (pets) => {
  if (!pets || !pets.length) {
    return false;
  }
  return pets.filter((pet) => pet.petType === PET_TYPE.DOG && !pet.deleted).length > 1;
};

const getFirstDog = (pets) => pets.find((pet) => pet.petType === PET_TYPE.DOG);

const isOrderFullyRefunded = (order) => {
  return (
    isOrderCharged(order) && order.amountRefunded > 0 && order.amountCharge === order.amountRefunded
  );
};

const getChargeStatus = (order) => {
  if (isOrderFullyRefunded(order)) {
    return 'fully refunded';
  }
  return order.lastChargeStatus || 'not charged';
};

// Gets the index for the given pet by keyId
// If not found, return first pet
const getPetIndex = (keyId, pets) => {
  const petIndex = pets.findIndex((pet) => pet.key.id === keyId);
  return petIndex > -1 ? petIndex : 0;
};

const getWeightStatus = (currentWeight, weight) => {
  const weightDelta = Math.abs(currentWeight - weight);
  const weightStatus = {status: '', description: ''};
  if (currentWeight === weight) {
    weightStatus.status = 'Ideal';
    weightStatus.description = 'Looking good!';
  } else if (weight && currentWeight > weight) {
    weightStatus.status = 'Overweight';
    weightStatus.description = `Looking to lose ${weightDelta} lb${weightDelta > 1 ? 's' : ''}.`;
  } else if (weight) {
    weightStatus.status = 'Underweight';
    weightStatus.description = `Looking to gain ${weightDelta} lb${weightDelta > 1 ? 's' : ''}.`;
  }
  return weightStatus;
};

const getMostRecentOrderForDisplay = (customer, customerOrders) => {
  // Show most recent order for one-time trial customers.
  if (isInOneTimeTrial(customer)) {
    return customerOrders.find((order) => !order.deleted);
  }

  // Only show most recent subscription order for all other customers.
  return customerOrders.find((order) => {
    return !order.deleted && order.orderSource === ORDER_SOURCE.ORDER_SOURCE_SUBSCRIPTION;
  });
};

const getOrderStatus = (order) => {
  if (order.status === ORDER_STATUS.DELIVERED) {
    return 'Delivered';
  }
  if (order.trackingStatus === 'out_for_delivery') {
    return 'Out for delivery';
  }
  if (order.status === ORDER_STATUS.SHIPPED || order.status === ORDER_STATUS.DELIVERY_FAILED) {
    return 'Shipped';
  }
  return 'Prepped';
};

export {
  buildCouponsMap,
  buildOnboardingServingDetails,
  buildRecipesAndGramsList,
  calculateOnboardingProgress,
  countCompletedSurveys,
  countRemainingSurveys,
  getChargeStatus,
  getFirstDog,
  getMostRecentOrderForDisplay,
  getOnboardingPageName,
  getOrderStatus,
  getPetIndex,
  getWeightStatus,
  hasFeatureFoodFixedSku,
  hasFeatureFoodPersonalized,
  hasMoreThanOneDog,
  isCustomizedMealPlanItem,
  isEligibleToChangeSubscription,
  isEligibleToReferFriends,
  isOnFlexibleMealPlan,
  isOnPersonalizedMealPlan,
  isOrderCharged,
  isValidOrderForOnboarding,
  isInOneTimeTrial,
  orderHasShipped,
  selfCancellationHref,
  STRIPE_INVOICE_URL,
};
